import { useState, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/useToast'
import { databaseKeys } from '@/hooks/useDatabase'
import { AgentProxyService } from '@/services/agentProxyService'

interface BulkDropItem {
  type: 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'user'
  name: string
  database: string
  schema?: string
}

interface BulkDropOperationData {
  items: BulkDropItem[]
  connectionId: string
  cascade?: boolean
}

interface BulkDropResult {
  successful: BulkDropItem[]
  failed: { item: BulkDropItem; error: string }[]
}

interface BulkDropHookOptions {
  onSuccess?: (result: BulkDropResult) => void
  maxConcurrency?: number // Maximum number of concurrent operations (default: unlimited)
}

export function useBulkDropOperation(options?: BulkDropHookOptions) {
  const [isBulkDropDialogOpen, setIsBulkDropDialogOpen] = useState(false)
  const [bulkDropData, setBulkDropData] = useState<BulkDropOperationData | null>(null)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const isCancelledRef = useRef(false)
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const dropSingleItem = async (item: BulkDropItem, connectionId: string, cascade = false): Promise<void> => {
    const { type, name, database, schema } = item

    switch (type) {
      case 'database':
        await AgentProxyService.dropDatabase(connectionId, name)
        break

      case 'schema':
        await AgentProxyService.dropSchema(connectionId, database, name, cascade)
        break

      case 'table':
        await AgentProxyService.dropTable(connectionId, database, name, { 
          schemaName: schema, 
          cascade 
        })
        break

      case 'view':
        await AgentProxyService.dropView(connectionId, database, name, schema, cascade)
        break

      case 'function':
        // Function operations not yet implemented in agent proxy
        throw new Error('Function drop operation not yet implemented')

      case 'procedure':
        // Procedure operations not yet implemented in agent proxy
        throw new Error('Procedure drop operation not yet implemented')

      case 'trigger':
        // Trigger operations not yet implemented in agent proxy
        throw new Error('Trigger drop operation not yet implemented')

      case 'sequence':
        // Sequence operations not yet implemented in agent proxy
        throw new Error('Sequence drop operation not yet implemented')

      case 'index':
        await AgentProxyService.dropIndex(connectionId, database, name, schema)
        break

      case 'extension':
        // Extension operations not yet implemented in agent proxy
        throw new Error('Extension drop operation not yet implemented')

      case 'event':
        // Event operations not yet implemented in agent proxy
        throw new Error('Event drop operation not yet implemented')

      case 'user':
        await AgentProxyService.dropUser(connectionId, database, name)
        break

      default:
        throw new Error(`Unsupported drop operation: ${type as string}`)
    }
  }

  const bulkDropMutation = useMutation({
    mutationFn: async ({ items, connectionId, cascade = false }: BulkDropOperationData): Promise<BulkDropResult> => {
      const successful: BulkDropItem[] = []
      const failed: { item: BulkDropItem; error: string }[] = []
      const maxConcurrency = options?.maxConcurrency || 10 // Default to 10 concurrent operations
      
      // Initialize progress and reset cancelled state
      setProgress({ completed: 0, total: items.length })
      isCancelledRef.current = false

      // Helper function to process items in batches with concurrency limit
      const processBatch = async (batch: BulkDropItem[]) => {
        const dropPromises = batch.map(async (item) => {
          // Check if cancelled before processing
          if (isCancelledRef.current) {
            return { success: false, item, error: 'Operation cancelled' }
          }
          
          try {
            await dropSingleItem(item, connectionId, cascade)
            // Update progress after successful deletion
            setProgress(prev => ({ ...prev, completed: prev.completed + 1 }))
            return { success: true, item }
          } catch (error) {
            // Update progress even on failure
            setProgress(prev => ({ ...prev, completed: prev.completed + 1 }))
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
            return { success: false, item, error: errorMessage }
          }
        })

        return Promise.allSettled(dropPromises)
      }

      // If we have a reasonable number of items, process them all concurrently
      if (items.length <= maxConcurrency) {
        const results = await processBatch(items)
        
        // Process results
        for (const [index, result] of results.entries()) {
          if (result.status === 'fulfilled') {
            const { success, item, error } = result.value
            if (success) {
              successful.push(item)
            } else {
              failed.push({ item, error: error || 'Unknown error occurred' })
            }
          } else {
            // Promise was rejected (shouldn't happen since we catch errors above, but just in case)
            const item = items[index]
            const errorMessage = result.reason instanceof Error ? result.reason.message : 'Promise rejected unexpectedly'
            failed.push({ item, error: errorMessage })
          }
        }
      } else {
        // For large numbers of items, process in batches to avoid overwhelming the server
        for (let i = 0; i < items.length; i += maxConcurrency) {
          // Check if cancelled before processing next batch
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (isCancelledRef.current) {
            // Mark remaining items as cancelled
            for (let j = i; j < items.length; j++) {
              failed.push({ item: items[j], error: 'Operation cancelled' })
            }
            break
          }
          
          const batch = items.slice(i, i + maxConcurrency)
          const results = await processBatch(batch)
          
          // Process results for this batch
          for (const [batchIndex, result] of results.entries()) {
            const originalIndex = i + batchIndex
            if (result.status === 'fulfilled') {
              const { success, item, error } = result.value
              if (success) {
                successful.push(item)
              } else {
                failed.push({ item, error: error || 'Unknown error occurred' })
              }
            } else {
              // Promise was rejected
              const item = items[originalIndex]
              const errorMessage = result.reason instanceof Error ? result.reason.message : 'Promise rejected unexpectedly'
              failed.push({ item, error: errorMessage })
            }
          }
        }
      }

      return { successful, failed }
    },
    onSuccess: (result, { connectionId }) => {
      const { successful, failed } = result
      
      // Call the external onSuccess callback if provided
      if (options?.onSuccess) {
        options.onSuccess(result)
      }
      
      // Check if operation was cancelled
      const wasCancelled = failed.some(f => f.error === 'Operation cancelled')
      
      // Show appropriate toast message
      if (wasCancelled && successful.length > 0) {
        showToast('warning', `Operation cancelled. Dropped ${successful.length} item${successful.length > 1 ? 's' : ''} before cancellation.`)
      } else if (successful.length > 0 && failed.length === 0) {
        showToast('success', `Successfully dropped ${successful.length} item${successful.length > 1 ? 's' : ''}`)
      } else if (successful.length > 0 && failed.length > 0) {
        showToast('warning', `Dropped ${successful.length} item${successful.length > 1 ? 's' : ''}, ${failed.length} failed`)
      } else if (failed.length > 0) {
        showToast('error', `Failed to drop ${failed.length} item${failed.length > 1 ? 's' : ''}`)
      }

      // Log failed operations
      if (failed.length > 0) {
        console.error('Bulk drop failures:', failed)
      }
      
      // Invalidate relevant queries to refresh the UI
      const invalidatePromises = []
      
      // Always invalidate databases query for connection
      invalidatePromises.push(
        queryClient.invalidateQueries({ queryKey: databaseKeys.list(connectionId) })
      )

      // Collect databases by whether they were deleted or just had objects deleted
      const deletedDatabases = new Set<string>()
      const affectedDbSchemas = new Set<string>()
      
      for (const item of successful) {
        if (item.type === 'database') {
          deletedDatabases.add(item.database)
        } else {
          // Track database+schema combination for more precise invalidation
          const key = `${item.database}|${item.schema || 'default'}`
          affectedDbSchemas.add(key)
        }
      }
      
      for (const { item } of failed) {
        if (item.type !== 'database') {
          // Track database+schema combination for failed items too
          const key = `${item.database}|${item.schema || 'default'}`
          affectedDbSchemas.add(key)
        }
      }

      // For deleted databases, remove queries from cache instead of invalidating
      for (const database of deletedDatabases) {
        queryClient.removeQueries({ queryKey: databaseKeys.schemas(connectionId, database) })
        queryClient.removeQueries({ queryKey: databaseKeys.tables(connectionId, database) })
        queryClient.removeQueries({ queryKey: ['databases', 'views', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'functions', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'procedures', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'triggers', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'sequences', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'indexes', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'extensions', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'events', connectionId, database] })
        queryClient.removeQueries({ queryKey: ['databases', 'users', connectionId, database] })
        // Also remove database details query
        queryClient.removeQueries({ queryKey: [...databaseKeys.all, 'details', connectionId, database] })
      }

      // For affected database+schema combinations (where objects were deleted but database still exists), invalidate queries
      for (const dbSchemaKey of affectedDbSchemas) {
        const [database, schemaName] = dbSchemaKey.split('|')
        const schema = schemaName === 'default' ? undefined : schemaName
        
        invalidatePromises.push(
          queryClient.invalidateQueries({ queryKey: databaseKeys.schemas(connectionId, database), exact: false }),
          queryClient.invalidateQueries({ queryKey: databaseKeys.tables(connectionId, database, schema), exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'views', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'functions', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'procedures', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'triggers', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'sequences', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'indexes', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'extensions', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'events', connectionId, database], exact: false }),
          queryClient.invalidateQueries({ queryKey: ['databases', 'users', connectionId, database], exact: false })
        )
      }

      // If any databases were dropped, invalidate broader queries
      if (deletedDatabases.size > 0) {
        invalidatePromises.push(
          queryClient.invalidateQueries({ queryKey: ['databases'] })
        )
      }

      // Execute all invalidations
      Promise.all(invalidatePromises).catch(console.error)
      
      // Close the dialog and clear data after successful operation
      setIsBulkDropDialogOpen(false)
      setBulkDropData(null)
      setProgress({ completed: 0, total: 0 })
      isCancelledRef.current = false
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during bulk drop'
      showToast('error', errorMessage)
      console.error('Bulk drop operation failed:', error)
      
      // Reset state on error
      setIsBulkDropDialogOpen(false)
      setBulkDropData(null)
      setProgress({ completed: 0, total: 0 })
      isCancelledRef.current = false
    }
  })

  const initiateBulkDropOperation = (items: BulkDropItem[], connectionId: string) => {
    setBulkDropData({ items, connectionId })
    setIsBulkDropDialogOpen(true)
  }

  const confirmBulkDrop = (cascade = false) => {
    if (bulkDropData) {
      bulkDropMutation.mutate({ ...bulkDropData, cascade })
    }
  }

  const cancelBulkDrop = () => {
    // If operation is in progress, set cancelled flag
    if (bulkDropMutation.isPending) {
      isCancelledRef.current = true
      // Don't close dialog immediately, let the mutation handle cleanup
    } else {
      // If not loading, close dialog immediately
      setIsBulkDropDialogOpen(false)
      setBulkDropData(null)
      setProgress({ completed: 0, total: 0 })
      isCancelledRef.current = false
    }
  }

  return {
    isBulkDropDialogOpen,
    bulkDropData,
    isBulkDropLoading: bulkDropMutation.isPending,
    progress,
    initiateBulkDropOperation,
    confirmBulkDrop,
    cancelBulkDrop,
    bulkDropResult: bulkDropMutation.data
  }
}
